import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import EmailUsageIndicator from './EmailUsageIndicator'
import PushToggle from './PushToggle'
import apiClient from '../services/api'
import { getApprovalCount } from '../services/approvals'
import { sidebar } from '../design/tokens'

const SEEN_REPLIES_KEY = 'spread_seen_reply_ids'

interface NavItem {
  label: string
  path: string
  href?: string
  icon: React.ReactNode
}

interface NavGroup {
  heading?: string
  items: NavItem[]
}

const iconChat = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
)
const iconLeads = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
)
const iconMail = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
)
const iconReply = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
)
const iconFlows = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="6" r="2"/><circle cx="5" cy="18" r="2"/><path d="M7 6h7a3 3 0 0 1 0 6H7"/><path d="M7 18h12"/></svg>
)
/** ライン。ベルトと、その上を流れる荷物ひとつ。 */
const iconOffice = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="4" width="6" height="6" rx="1"/><path d="M3 15h18"/><path d="M6 19v-4"/><path d="M12 19v-4"/><path d="M18 19v-4"/></svg>
)
const iconSettings = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
)

/** The rows, in the order a day goes: ask, work,材料, everything else.
 *
 *  It used to be grouped by 「ワークフロー / システム」, which said where a
 *  screen came from rather than what it is for — and once 「フロー」 arrived
 *  three different rows answered 「どこまで行ったか」 (the dashboard's numbers,
 *  the flows, the mail desk) with nothing to say which one to open.
 *
 *  So the headings name the **part they play**:
 *
 *  * no heading — the chat, which is where a question goes before it is work;
 *  * 仕事 — the three screens with something of yours waiting in them. Their
 *    order is the order work passes through: which batch is where (フロー),
 *    the mail to read and send (メール), the answers that came back (返信);
 *  * 材料 — the companies the work is made of;
 *  * ほか — the report and the settings. The dashboard is a summary of what
 *    already happened; it is not where anything is decided, so it is not in
 *    the way of the three rows that are.
 *
 *  **No badge on 「フロー」**, deliberately. The count it would carry —
 *  「あなた待ち」 — is the same drafts the mail row already counts, and the
 *  badge belongs where the reading is done, not where it is listed.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { label: 'AI営業チャット', path: '/chat', icon: iconChat },
    ],
  },
  {
    heading: '仕事',
    items: [
      // 「どのリストがどこまで行ったか」. The desk below answers that for the
      // whole account, which stops being an answer once two lists are being
      // worked at once; this row is the same question per flow (FlowsPage).
      { label: 'フロー', path: '/flows', icon: iconFlows },
      // 「ライン」. 「どこまで行ったか」の一つ手前、**「いま本当に動いて
      // いるのか」** に答える画面 (OfficePage)。フローの一覧と同じ数を別の
      // 形で出しているのではなく、答えているのが別の問いなので行を分けて
      // いる — 一覧は読んで確かめる画面、こちらは**見れば分かる**画面。
      { label: 'ライン', path: '/office', icon: iconOffice },
      // One row, where there used to be two: 「下書きの確認」 and 「メール」
      // were the same screen asked for twice, and nothing said which of them
      // held the sent mail. The desk is the drafts *and* the four tabs around
      // them (MailDeskPage); /mail keeps the two things that are not one of
      // the five — composing a one-off, and the templates — and the desk
      // links to both.
      { label: 'メール', path: '/drafts', icon: iconMail },
      { label: '返信', path: '/reply-inbox', icon: iconReply },
    ],
  },
  {
    heading: '材料',
    items: [
      { label: '営業リスト', path: '/leads', icon: iconLeads },
    ],
  },
  {
    // ダッシュボードはここから外した。あの画面の5枚のカードが答えていた質問
    // （何通送った・下書きは何件）は、フローの盤の列そのもので、同じ数を2つの
    // 入り口に置くと、片方が古くなった日にどちらを信じるかという問題だけが
    // 残る。開封率と件名ごとの成績はまだあの画面にしか無いので、フローの
    // 「ほかに見るもの」から行く（画面もURLもそのまま）。
    heading: 'ほか',
    items: [
      { label: '設定', path: '/settings', icon: iconSettings },
    ],
  },
]

interface AppSidebarProps {
  className?: string
}

const AppSidebar: React.FC<AppSidebarProps> = ({ className }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [unreadReplies, setUnreadReplies] = useState(0)
  const [pendingApprovals, setPendingApprovals] = useState(0)

  useEffect(() => {
    const fetchReplies = async () => {
      try {
        const res = await apiClient.get('/email-campaigns/reply-summary')
        const repliedIds: number[] = res.data.replied_ids ?? []
        if (location.pathname === '/reply-inbox') {
          localStorage.setItem(SEEN_REPLIES_KEY, JSON.stringify(repliedIds))
          setUnreadReplies(0)
        } else {
          const seen: number[] = JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || '[]')
          setUnreadReplies(repliedIds.filter(id => !seen.includes(id)).length)
        }
      } catch { /* ignore */ }
    }
    fetchReplies()
  }, [location.pathname])

  // Unlike the reply badge, this one has no "seen" state in localStorage: a
  // draft is waiting until it is sent or thrown away, and looking at it does
  // not make it not waiting.
  useEffect(() => {
    let cancelled = false
    getApprovalCount()
      .then(count => { if (!cancelled) setPendingApprovals(count.total) })
      .catch(() => { /* the row still works without the number */ })
    return () => { cancelled = true }
  }, [location.pathname])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path)
    return (
      <div
        key={item.path}
        onClick={() => navigate(item.href || item.path)}
        role="link"
        tabIndex={0}
        aria-current={active ? 'page' : undefined}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigate(item.href || item.path)
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          padding: '0.5rem 0.625rem',
          // 37px without this: a phone finger needs 44px, and these are the
          // rows that switch screens — the one tap this app cannot afford to
          // miss twice.
          minHeight: 44,
          boxSizing: 'border-box',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: active ? '600' : '400',
          color: active ? sidebar.textActive : sidebar.text,
          backgroundColor: active ? sidebar.bgActive : 'transparent',
          marginBottom: '1px',
          position: 'relative',
          transition: 'background-color 0.12s, color 0.12s',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = sidebar.bgHover }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent' }}
      >
        <span style={{ opacity: active ? 1 : 0.7, display: 'flex', alignItems: 'center' }}>
          {item.icon}
        </span>
        {item.label}
        {/* On the mail row, because that is where the drafts are now. It used
            to be keyed to '/approvals', which no row has had since that path
            became a redirect — so the count was fetched on every navigation
            and drawn nowhere. */}
        {item.path === '/drafts' && pendingApprovals > 0 && (
          <span
            aria-label={`${pendingApprovals}件の承認待ち`}
            style={{
              marginLeft: 'auto',
              backgroundColor: '#93C5FD',
              color: '#1E3A8A',
              borderRadius: '9999px',
              fontSize: '0.625rem',
              fontWeight: '700',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {pendingApprovals > 99 ? '99+' : pendingApprovals}
          </span>
        )}
        {item.path === '/reply-inbox' && unreadReplies > 0 && (
          <span
            aria-label={`${unreadReplies}件の未読返信`}
            style={{
              marginLeft: 'auto',
              backgroundColor: '#FCA5A5',
              color: '#7F1D1D',
              borderRadius: '9999px',
              fontSize: '0.625rem',
              fontWeight: '700',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {unreadReplies > 9 ? '9+' : unreadReplies}
          </span>
        )}
      </div>
    )
  }

  return (
    <aside id="app-sidebar-nav" className={className} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      width: '220px',
      backgroundColor: sidebar.bg,
      // No right edge: the colour change is the edge.
      borderRight: 'none',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 50,
      overflowY: 'auto',
    }}>
      {/* Logo */}
      <div
        style={{ padding: '1.25rem 1rem 0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        onClick={() => navigate('/')}
        role="link"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigate('/')
          }
        }}
        aria-label="ホームへ"
      >
        <div style={{
          width: '28px', height: '28px', borderRadius: '6px',
          backgroundColor: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="17" height="17" viewBox="0 0 512 512" fill="none" aria-hidden="true">
            <polygon points="446,88 84,262 220,312" fill="#e3edff" />
            <polygon points="446,88 220,312 252,428" fill="#ffffff" />
          </svg>
        </div>
        <span style={{ fontSize: '1rem', fontWeight: '700', color: sidebar.textActive, letterSpacing: '-0.02em' }}>
          Spread
        </span>
      </div>

      {/* Nav sections */}
      <nav style={{ padding: '0.25rem 0.5rem', flex: 1 }} aria-label="主要ナビゲーション">
        {NAV_GROUPS.map((group, gi) => (
          // The gap between groups is what says where one ends, so it is
          // bigger than the gap between the rows inside one. The heading
          // reads as a label rather than as a row: darker than the muted grey
          // it was (2.5:1 at 11px), one size up.
          <div key={group.heading || `g-${gi}`} style={{ marginBottom: '1.25rem' }}>
            {group.heading && (
              <p style={{
                fontSize: '0.75rem', fontWeight: '600', color: sidebar.textMuted,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '0.5rem 0.5rem 0.375rem', margin: 0,
              }}>
                {group.heading}
              </p>
            )}
            {group.items.map(renderNavItem)}
          </div>
        ))}

      </nav>

      {/* Footer */}
      <div style={{ padding: '0.75rem 0.75rem 1rem', borderTop: `1px solid ${sidebar.border}` }}>
        {/* Beside the approval badge above, which is what it announces. */}
        <PushToggle />
        <div style={{ marginBottom: '0.625rem' }}>
          <EmailUsageIndicator onDark />
        </div>
        {user && (
          <p style={{
            fontSize: '0.75rem', color: sidebar.textMuted,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            margin: '0 0 0.5rem', padding: '0 0.125rem',
          }}>
            {user.email}
          </p>
        )}
        <button
          onClick={handleLogout}
          style={{
            width: '100%', padding: '0.4375rem', minHeight: 44,
            boxSizing: 'border-box', border: `1px solid ${sidebar.borderStrong}`,
            borderRadius: '6px', backgroundColor: 'transparent', color: sidebar.text,
            cursor: 'pointer', fontSize: '0.8125rem', fontWeight: '500',
            transition: 'background-color 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = sidebar.bgHover }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
        >
          ログアウト
        </button>
      </div>
    </aside>
  )
}

export default AppSidebar
